import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type BackgroundType = 'purple' | 'hexagon' | 'cyber' | 'flowing' | 'hitech' | 'honeycomb' | 'off';
export type DarkOverlayType = 'default' | 'dark' | 'blue' | 'minimal' | 'none';
export type LightOverlayType = 'light-default' | 'light-bright' | 'light-warm' | 'light-minimal' | 'none';
export type OverlayType = DarkOverlayType | LightOverlayType;

interface BackgroundSettings {
  backgroundType: BackgroundType;
  darkOverlayType: DarkOverlayType;
  lightOverlayType: LightOverlayType;
  edgeFeather: boolean;
  playbackSpeed: number;
}

interface BackgroundContextType {
  settings: BackgroundSettings;
  setBackgroundType: (type: BackgroundType) => void;
  setDarkOverlayType: (type: DarkOverlayType) => void;
  setLightOverlayType: (type: LightOverlayType) => void;
  setEdgeFeather: (enabled: boolean) => void;
  setPlaybackSpeed: (speed: number) => void;
  cycleBackground: () => void;
  cycleOverlay: () => void;
  resetSettings: () => void;
  getCurrentOverlay: () => OverlayType;
  isEasterEggActive: boolean;
  toggleEasterEgg: () => void;
  notificationMessage: string | null;
  showNotification: (message: string) => void;
}

const BackgroundContext = createContext<BackgroundContextType | undefined>(undefined);

const DEFAULT_SETTINGS: BackgroundSettings = {
  backgroundType: 'hexagon',
  darkOverlayType: 'minimal',
  lightOverlayType: 'light-default',
  edgeFeather: true,
  playbackSpeed: 0.8,
};

const STORAGE_KEY = 'tesseract-background-settings';
const EASTER_EGG_STORAGE_KEY = 'tesseract-background-easter-egg';
const NOTIFICATION_TIMEOUT = 3000;

// Helper function to detect current theme
const useTheme = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const updateTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    // Initial check
    updateTheme();

    // Watch for theme changes
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
};

export const BackgroundProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<BackgroundSettings>(DEFAULT_SETTINGS);
  const [isEasterEggActive, setIsEasterEggActive] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState<string | null>(null);
  const [notificationTimeout, setNotificationTimeout] = useState<NodeJS.Timeout | null>(null);
  const isDark = useTheme();

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      }
    } catch (error) {
      console.error('Failed to load background settings:', error);
    }
  }, []);

  // Load easter egg state from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(EASTER_EGG_STORAGE_KEY);
      if (saved === 'true') {
        setIsEasterEggActive(true);
      }
    } catch (error) {
      console.error('Failed to load easter egg state:', error);
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save background settings:', error);
    }
  }, [settings]);

  const setBackgroundType = (type: BackgroundType) => {
    setSettings(prev => ({ ...prev, backgroundType: type }));
  };

  const setDarkOverlayType = (type: DarkOverlayType) => {
    setSettings(prev => ({ ...prev, darkOverlayType: type }));
  };

  const setLightOverlayType = (type: LightOverlayType) => {
    setSettings(prev => ({ ...prev, lightOverlayType: type }));
  };

  const setEdgeFeather = (enabled: boolean) => {
    setSettings(prev => ({ ...prev, edgeFeather: enabled }));
  };

  const setPlaybackSpeed = (speed: number) => {
    setSettings(prev => ({ ...prev, playbackSpeed: speed }));
  };

  const getCurrentOverlay = (): OverlayType => {
    return isDark ? settings.darkOverlayType : settings.lightOverlayType;
  };

  const cycleBackground = () => {
    const backgrounds: BackgroundType[] = ['purple', 'hexagon', 'cyber', 'flowing', 'hitech', 'honeycomb', 'off'];
    const currentIndex = backgrounds.indexOf(settings.backgroundType);
    const nextIndex = (currentIndex + 1) % backgrounds.length;
    setBackgroundType(backgrounds[nextIndex]);
  };

  const cycleOverlay = () => {
    if (isDark) {
      const overlays: DarkOverlayType[] = ['default', 'dark', 'blue', 'minimal'];
      const currentIndex = overlays.indexOf(settings.darkOverlayType);
      const nextIndex = (currentIndex + 1) % overlays.length;
      setDarkOverlayType(overlays[nextIndex]);
    } else {
      const overlays: LightOverlayType[] = ['light-default', 'light-bright', 'light-warm', 'light-minimal'];
      const currentIndex = overlays.indexOf(settings.lightOverlayType);
      const nextIndex = (currentIndex + 1) % overlays.length;
      setLightOverlayType(overlays[nextIndex]);
    }
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  const toggleEasterEgg = () => {
    const newState = !isEasterEggActive;
    setIsEasterEggActive(newState);
    try {
      localStorage.setItem(EASTER_EGG_STORAGE_KEY, newState.toString());
    } catch (error) {
      console.error('Failed to save easter egg state:', error);
    }
  };

  const showNotification = (message: string) => {
    // Clear existing timeout to prevent multiple notifications
    if (notificationTimeout) {
      clearTimeout(notificationTimeout);
    }
    
    setNotificationMessage(message);
    const timeoutId = setTimeout(() => {
      setNotificationMessage(null);
      setNotificationTimeout(null);
    }, NOTIFICATION_TIMEOUT);
    
    setNotificationTimeout(timeoutId);
  };

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (notificationTimeout) {
        clearTimeout(notificationTimeout);
      }
    };
  }, [notificationTimeout]);

  return (
    <BackgroundContext.Provider
      value={{
        settings,
        setBackgroundType,
        setDarkOverlayType,
        setLightOverlayType,
        setEdgeFeather,
        setPlaybackSpeed,
        cycleBackground,
        cycleOverlay,
        resetSettings,
        getCurrentOverlay,
        isEasterEggActive,
        toggleEasterEgg,
        notificationMessage,
        showNotification,
      }}
    >
      {children}
      {notificationMessage && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg z-[100] transition-opacity duration-500 animate-pulse">
          {notificationMessage}
        </div>
      )}
    </BackgroundContext.Provider>
  );
};

export const useBackground = () => {
  const context = useContext(BackgroundContext);
  if (context === undefined) {
    throw new Error('useBackground must be used within a BackgroundProvider');
  }
  return context;
};